// StatsRowV3 — 4 compact stat cards (credits, saved leads, active campaigns,
// reputation). Only `credits` uses the real user record; leads/campaigns/
// reputation are demo placeholders (small "🧪 Demo" hint under the card).
//
// The credits card is also the low-balance badge host — pulse dot appears
// when `credits < 500`. Clicking either the badge or the card opens the
// TopUpModalV3 controlled by the parent.
import React from "react";
import { useTranslation } from "react-i18next";
import { Coins, Users, Send, Trophy } from "lucide-react";
import StatCardV3 from "../components/StatCardV3";

const LOW_BALANCE_THRESHOLD = 500;

function formatCreditsFull(n) {
    if (n == null) return "0";
    return n.toLocaleString("it-IT");
}

export default function StatsRowV3({
    credits = 0,
    leadsCount = 0,
    campaignsCount = 0,
    reputationScore = 0,
    onOpenTopUp,
}) {
    const { t } = useTranslation("dashboard_v3");
    const lowBalance = (credits ?? 0) < LOW_BALANCE_THRESHOLD;

    const reputationPct = Math.min(100, Math.max(0, (reputationScore / 1000) * 100));

    return (
        <section aria-label={t("stats.row_label")}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCardV3
                    testId="dashboard-v3-stats-credits"
                    icon={Coins}
                    label={t("stats.credits_label")}
                    value={formatCreditsFull(credits)}
                    ctaLabel={t("stats.credits_cta")}
                    onCtaClick={onOpenTopUp}
                    badge={{
                        active: lowBalance,
                        tooltip: t("stats.credits_lowbalance_tooltip"),
                        onClick: onOpenTopUp,
                    }}
                    delay={0}
                />
                <StatCardV3
                    testId="dashboard-v3-stats-leads"
                    icon={Users}
                    label={t("stats.leads_label")}
                    value={leadsCount.toLocaleString("it-IT")}
                    ctaLabel={t("stats.leads_cta")}
                    ctaHref="/leads/saved"
                    demo
                    demoLabel={t("stats.demo_label")}
                    delay={0.05}
                />
                <StatCardV3
                    testId="dashboard-v3-stats-campaigns"
                    icon={Send}
                    label={t("stats.campaigns_label")}
                    value={campaignsCount.toLocaleString("it-IT")}
                    ctaLabel={t("stats.campaigns_cta")}
                    ctaHref="/outreach/campaigns"
                    demo
                    demoLabel={t("stats.demo_label")}
                    delay={0.1}
                />
                {/* Reputation with progress bar */}
                <div
                    data-testid="dashboard-v3-stats-reputation"
                    aria-label={t("stats.reputation_label")}
                    className="rounded-2xl border p-5 transition-colors"
                    style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#FFFFFF")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#1A1A1A")}
                >
                    <div className="flex items-start justify-between">
                        <span
                            className="h-9 w-9 rounded-xl grid place-items-center"
                            style={{ backgroundColor: "rgba(255, 255, 255,0.12)" }}
                        >
                            <Trophy className="h-4 w-4" style={{ color: "#FFFFFF" }} aria-hidden />
                        </span>
                    </div>
                    <p className="mt-4 text-3xl font-semibold tabular-nums tracking-tight text-[#FFFFFF]">
                        {reputationScore.toLocaleString("it-IT")}
                        <span className="text-sm font-normal text-[#CCCCCC] ml-1">
                            {t("stats.reputation_max")}
                        </span>
                    </p>
                    <p className="mt-1 text-xs text-[#CCCCCC]">{t("stats.reputation_label")}</p>
                    <div
                        className="mt-4 h-1 rounded-full overflow-hidden"
                        style={{ backgroundColor: "#1A1A1A" }}
                        role="progressbar"
                        aria-valuenow={reputationScore}
                        aria-valuemin={0}
                        aria-valuemax={1000}
                    >
                        <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${reputationPct}%`, backgroundColor: "#FFFFFF" }}
                        />
                    </div>
                    <p
                        className="mt-3 text-[10px] uppercase tracking-widest text-[#71717A] opacity-50"
                        style={{ filter: "grayscale(1)" }}
                    >
                        🧪 {t("stats.demo_label")}
                    </p>
                </div>
            </div>
        </section>
    );
}
